import { useEffect, useState } from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { ChevronDown, ChevronRight } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, withWeight } from '../../theme';
import { CURSOR_POINTER } from '../../utils/webStyles';
import { useWebHover } from '../../hooks/useWebHover';
import { formatRelativeTime } from '../../utils/time';
import { initials } from '../../utils/initials';
import { PLAN_PARTY_TYPE_META, partyDisplayName } from './planParties';
import type { PlanParty, DefensePlanAssignment } from '../../types';

// The most recent "Ask for an update" send for a party, across every
// assignment it covers (DefensePlanPartyEditSheet's Ask action stamps
// lastAskedAt on each of that party's assignments, not on the party itself
// — there is no single field to read). Null when never asked.
function latestAskedAt(
  partyId: string,
  assignments: Record<string, DefensePlanAssignment>
): DefensePlanAssignment['lastAskedAt'] | null {
  let latest: DefensePlanAssignment['lastAskedAt'] | null = null;
  for (const a of Object.values(assignments)) {
    if (a.partyId !== partyId || !a.lastAskedAt) continue;
    if (!latest || a.lastAskedAt.toMillis() > latest.toMillis()) latest = a.lastAskedAt;
  }
  return latest;
}

// Editor-only compact roster: every party on this plan, and how many
// defenses each currently covers — "Big Bear Fire Safe Council · County ·
// 3 defenses". Tapping a row opens DefensePlanPartyEditSheet to rename,
// retype, or delete the party outright (which also unlinks it everywhere).
function PartyRow({
  party,
  defenseCount,
  askedAt,
  onPress,
  active,
}: {
  party: PlanParty;
  defenseCount: number;
  askedAt: DefensePlanAssignment['lastAskedAt'] | null;
  onPress: () => void;
  active: boolean;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  const name = partyDisplayName(party);
  const askedLabel = askedAt ? formatRelativeTime(askedAt, { suffix: 'ago' }) : null;
  const meta = [
    PLAN_PARTY_TYPE_META[party.type].label,
    `${defenseCount} ${defenseCount === 1 ? 'defense' : 'defenses'}`,
    askedLabel ? `Asked ${askedLabel}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  // Sept 28 polish: the same row as the People directory (initials, name,
  // one meta line), plus a chevron since it opens the party's editor.
  return (
    <Pressable
      onPress={onPress}
      testID={`plan-party-${party.id}`}
      accessibilityRole="button"
      accessibilityLabel={`Edit ${name}`}
      {...hoverProps}
      style={[
        styles.row,
        CURSOR_POINTER,
        {
          backgroundColor: active
            ? colors.pressedSurface
            : hovered
              ? colors.hoverSurface
              : 'transparent',
        },
      ]}>
      <View
        style={[styles.avatar, { backgroundColor: colors.surface, borderColor: colors.border }]}>
        <Text style={[styles.avatarText, { color: colors.textSecondary }]}>{initials(name)}</Text>
      </View>
      <View style={styles.rowText}>
        <Text style={[styles.rowName, { color: colors.text }]} numberOfLines={1}>
          {name}
        </Text>
        <Text style={[styles.rowMeta, { color: colors.textSecondary }]} numberOfLines={1}>
          {meta}
        </Text>
      </View>
      <ChevronRight size={14} color={colors.textTertiary} />
    </Pressable>
  );
}

// Collapsed by default (Sept 2026 layout polish — this roster reads as
// secondary to the feature list above it, and a long one shouldn't push the
// "In this plan" section further down the scroll on open). Collapses again
// whenever the plan changes, matching how the "Add a defense" region resets
// on a plan switch — see DefensePlanPanel's paletteExpanded.
export function PlanPartiesSection({
  planId,
  parties,
  assignments,
  onEditParty,
  activePartyId = null,
}: {
  planId: string | null;
  parties: Record<string, PlanParty>;
  assignments: Record<string, DefensePlanAssignment>;
  onEditParty: (partyId: string) => void;
  /** The party whose edit companion is open; its row keeps a fill. */
  activePartyId?: string | null;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    setExpanded(false);
  }, [planId]);

  if (!planId) return null;
  const list = Object.values(parties);
  if (list.length === 0) return null;

  const defenseCountFor = (partyId: string) => {
    const featureIds = new Set(
      Object.values(assignments)
        .filter((a) => a.partyId === partyId)
        .map((a) => a.featureId)
    );
    return featureIds.size;
  };

  const countLabel = `Who's doing it (${list.length})`;

  return (
    <View>
      <Pressable
        onPress={() => setExpanded((e) => !e)}
        testID="plan-parties-toggle"
        accessibilityRole="button"
        aria-expanded={expanded}
        accessibilityLabel={`${countLabel}, ${expanded ? 'expanded' : 'collapsed'}`}
        {...hoverProps}
        style={[styles.header, CURSOR_POINTER]}>
        {/* Hover brightens the label rather than filling a box that would
            start flush with the text. */}
        <Text
          style={[styles.sectionLabel, { color: hovered ? colors.text : colors.textTertiary }]}
          testID="plan-parties-section-label">
          {countLabel}
        </Text>
        {/* Rotated on a wrapper: a transform on the SVG itself didn't
            render on web, so the chevron vanished while open. */}
        <View style={expanded ? styles.chevronOpen : undefined}>
          <ChevronDown size={14} color={colors.textTertiary} />
        </View>
      </Pressable>
      {expanded && (
        <View style={styles.list}>
          {list.map((party) => (
            <PartyRow
              key={party.id}
              party={party}
              defenseCount={defenseCountFor(party.id)}
              askedAt={latestAskedAt(party.id, assignments)}
              onPress={() => onEditParty(party.id)}
              active={party.id === activePartyId}
            />
          ))}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    // The plan body's section rhythm (DefensePlanPanel's
    // sectionLabelSpaced): 24px above the label, 8px to the rows, less the
    // toggle's own 2px of vertical padding.
    marginTop: SPACING.lg - SPACING.xxs,
    paddingVertical: SPACING.xxs,
  },
  sectionLabel: {
    ...TYPOGRAPHY.overline,
  },
  chevronOpen: {
    transform: [{ rotate: '180deg' }],
  },
  list: {
    gap: 2,
    marginTop: SPACING.sm - SPACING.xxs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.smd,
    // Inside the column every tile and card shares, not past it.
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.sm,
    borderRadius: BORDER_RADIUS.md,
  },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: BORDER_RADIUS.full,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    ...withWeight(TYPOGRAPHY.caption, 'semibold'),
  },
  rowText: {
    flex: 1,
    minWidth: 0,
  },
  rowName: {
    ...TYPOGRAPHY.label,
  },
  rowMeta: {
    ...TYPOGRAPHY.caption,
    marginTop: 1,
  },
});
